import { Checkbox, DatePicker, Form, message, Modal } from '@maxtropy/components';
import { Col, Row, Space } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { MaintenanceMonthRecord } from './types';

interface EditMaintenanceMonthModalProps {
  open: boolean;
  record?: MaintenanceMonthRecord;
  existingMonths: string[];
  onCancel: () => void;
  onOk: (values: MaintenanceMonthRecord) => void;
}

interface ModalFormValues {
  month?: dayjs.Dayjs;
}

const EditMaintenanceMonthModal = (props: EditMaintenanceMonthModalProps) => {
  const { open, record, existingMonths, onCancel, onOk } = props;
  const [form] = Form.useForm<ModalFormValues>();
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const isEdit = !!record;
  const selectedMonth = Form.useWatch('month', form);

  const daysInMonth = useMemo(() => {
    return (selectedMonth ?? dayjs()).daysInMonth();
  }, [selectedMonth]);

  const dayOptions = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, index) => index + 1);
  }, [daysInMonth]);

  const checkedAll = selectedDays.length === dayOptions.length;
  const indeterminate = selectedDays.length > 0 && selectedDays.length < dayOptions.length;

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      month: record?.month ? dayjs(record.month) : dayjs(),
    });
    setSelectedDays(record?.days ?? []);
  }, [form, open, record]);

  useEffect(() => {
    setSelectedDays(days => days.filter(day => day <= daysInMonth));
  }, [daysInMonth]);

  const toggleDay = (day: number) => {
    setSelectedDays(days => {
      if (days.includes(day)) {
        return days.filter(item => item !== day);
      }
      return [...days, day].sort((a, b) => a - b);
    });
  };

  const handleCheckAll = (checked: boolean) => {
    setSelectedDays(checked ? dayOptions : []);
  };

  const handleOk = async () => {
    setConfirmLoading(true);
    const values = await form
      .validateFields()
      .then(res => res)
      .catch(() => null);
    setConfirmLoading(false);

    if (!values?.month) {
      return;
    }

    if (selectedDays.length === 0) {
      message.warning('请选择检修日期');
      return;
    }

    onOk({
      id: record?.id ?? Date.now(),
      month: isEdit ? record.month : values.month.format('YYYY-MM'),
      days: selectedDays,
    });
  };

  return (
    <Modal
      title={record ? '编辑检修月' : '新增检修月'}
      open={open}
      width={620}
      contentClassName="modal-form-content"
      destroyOnClose
      confirmLoading={confirmLoading}
      onCancel={onCancel}
      onOk={handleOk}
    >
      <Form form={form} layout="horizontal" labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
        <Form.Item
          label="检修月"
          name="month"
          rules={[
            { required: true, message: '请选择' },
            {
              validator: (_, value?: dayjs.Dayjs) => {
                if (isEdit || !value?.isValid()) {
                  return Promise.resolve();
                }

                const monthStr = value.format('YYYY-MM');
                return existingMonths.includes(monthStr)
                  ? Promise.reject(new Error('检修月不能重复'))
                  : Promise.resolve();
              },
            },
          ]}
        >
          <DatePicker
            disabled={isEdit}
            picker="month"
            format="YYYY-MM"
            placeholder="请选择"
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label="检修日期" required>
          <Space direction="vertical" size={8}>
            <Checkbox
              checked={checkedAll}
              indeterminate={indeterminate}
              onChange={e => handleCheckAll(e.target.checked)}
            >
              全选
            </Checkbox>
            <Row gutter={[8, 8]} className={styles.dayGrid}>
              {dayOptions.map(day => {
                const selected = selectedDays.includes(day);
                return (
                  <Col key={day}>
                    <button
                      className={selected ? styles.dayButtonSelected : styles.dayButton}
                      type="button"
                      onClick={() => toggleDay(day)}
                    >
                      {day}
                    </button>
                  </Col>
                );
              })}
            </Row>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditMaintenanceMonthModal;
